import { useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Button";
import { Icon } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Icon";

import { StreamingIcons } from "@/assets/iconFont/streaming-icons";
import { ButtonVariant } from "@/components/atoms/Button/types";
import { Text } from "@/components/atoms/Text";
import { isModalOpenAtom } from "@/constants/atoms";
import { useClassNames } from "@/utils/classNames";
import {
  checkIfMobileDevice,
  checkIfIOS,
  checkIfSafari,
  checkIfStandalone,
} from "@/utils/hooks/useDeviceDetection";

import styles from "./InstallModal.module.scss";
import { InstallModalProps } from "./types";

/**
 * The Install Modal appears when viewing prototypes on mobile browser if
 * it is not installed as a standalone app. On Android/Chromium devices it
 * prompts the user to install with a prebuilt dialog component. On iOS devices
 * it shows custom instructions to install the app to the homescreen since the
 * install dialog is not supported.
 *
 * @param className - Additional CSS class for styling
 * @returns the InstallModal component
 */
const InstallModal = ({ className }: InstallModalProps) => {
  const setGlobalModalOpen = useSetAtom(isModalOpenAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const isMobileDevice = checkIfMobileDevice();
  const isIOS = checkIfIOS();
  const isSafari = checkIfSafari();
  const isStandalone = checkIfStandalone();
  const cn = useClassNames(styles, styles["install-modal"], className);

  useEffect(() => {
    setGlobalModalOpen(isOpen);
  }, [isOpen, setGlobalModalOpen]);

  useEffect(() => {
    if (!isMobileDevice || isStandalone) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as any);
      setIsOpen(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // For iOS Safari (which doesn't support beforeinstallprompt)
    if (isIOS) {
      setIsOpen(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isMobileDevice, isIOS, isStandalone]);

  const handleInstall = async () => {
    if (!installPrompt) {
      setIsOpen(false);
      return;
    }

    try {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      setIsOpen(false);
    } catch (error) {
      setIsOpen(false);
    }
  };

  const handleDismiss = () => {
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cn}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Icon
            icon={StreamingIcons.WarningOutline}
            className={styles["warning"]}
          />
          <Text variant="h1" className={styles["title"]}>
            Prototypes look better in our app
          </Text>
          <Text variant="p" className={styles["description"]}>
            For the best viewing experience, please install this prototype to
            your device.
          </Text>
          {isIOS && (
            <ol className={styles["instructions"]}>
              {isSafari && (
                <li>
                  Tap the{" "}
                  <span className={styles["ios-button"]}>
                    <Icon icon={StreamingIcons.MoreH} /> More
                  </span>{" "}
                  button. Skip this step if your iOS version is less than 26.
                </li>
              )}
              <li>
                Tap the{" "}
                <span className={styles["ios-button"]}>
                  <Icon icon={StreamingIcons.IosShare} /> Share
                </span>{" "}
                button.
              </li>
              <li>Scroll down.</li>
              {!isSafari && (
                <li>
                  Tap the{" "}
                  <span className={styles["ios-button"]}>
                    <Icon icon={StreamingIcons.MoreH} /> More
                  </span>{" "}
                  button.
                </li>
              )}
              <li>
                Tap{" "}
                <span className={styles["ios-button"]}>
                  <Icon icon={StreamingIcons.Add} /> Add to Home Screen
                </span>
                .
              </li>
              <li>
                Keep "Open as Web App" toggled on and add to your home screen.
              </li>
            </ol>
          )}
          <div className={styles["button-group"]}>
            <Button onClick={handleDismiss} variant={ButtonVariant.Secondary}>
              Dismiss
            </Button>
            {!isIOS && <Button onClick={handleInstall}>Install</Button>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { InstallModal };
